<template>
  <div>
    <centered-card-page
      title="Templates"
      sub-title="Manage your document templates for your Inscribe account"
    >
      <template slot="buttons">
        <label class="btn btn-default mt-3">
          Upload template
          <input
            multiple
            type="file"
            accept="application/pdf, image/jpg, image/jpeg, image/png"
            style="display: none"
            @change="uploadFile"
          />
        </label>
      </template>
      <div>
        <h5>What are templates?</h5>
        <p>
          Fraudulent documents are often created by modifying a real or fake
          document which is used as a template. On this page you can upload
          documents which you would like to check against any future documents
          you come across. The results of this method are returned in the
          document results page for a customer under the Databases column. The
          results are also returned in our API under the
          <a href="https://docs.inscribe.ai/#check-document">
            Check Document
          </a>
          response under the key "templates".
        </p>
      </div>
    </centered-card-page>
    <centered-page v-if="templates.length > 0">
      <div class="w-100 mb-5">
        <div class="ui-table card">
          <table class="table">
            <thead>
              <tr>
                <th scope="col">Filename</th>
                <th scope="col">Created At</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(template, index) in templates" :key="index">
                <td id="templateFilename" width="50%" class="fs-exclude">
                  {{ template.filename }}
                </td>
                <td width="20%">
                  {{ formatFromNow(template.created_at) }}
                </td>
                <td>
                  <div v-if="'id' in template" class="d-flex">
                    <button
                      type="button"
                      class="btn btn-sm btn-remove mr-2"
                      @click="deleteTemplate(template)"
                    >
                      Delete
                    </button>
                    <a
                      :href="getSanitizedUrl(template.original_template_url)"
                      class="btn btn-sm btn-default"
                      target="_blank"
                    >
                      Open
                    </a>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </centered-page>
  </div>
</template>

<script>
import uuid from 'uuid/v4';
import { mapState } from 'vuex';
import { CenteredCardPage, CenteredPage } from '@inscribe/ui-kit';
import { $event } from '../../bus';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { formatFromNow } from '@/utils/date';

export default {
  components: {
    'centered-page': CenteredPage,
    'centered-card-page': CenteredCardPage,
  },
  data() {
    return {
      dragging: false,
      toBeUploaded: [],
      newEntry: {
        name: null,
        address: null,
        phone_number: null,
      },
    };
  },
  computed: {
    ...mapState(['templates']),
  },
  mounted() {
    this.$store.dispatch('getTemplates');
  },
  methods: {
    formatFromNow,
    getSanitizedUrl(url) {
      return sanitizeUrl(url);
    },
    uploadFile(event) {
      const self = this;

      for (const file of event.target.files) {
        self.toBeUploaded.push(file);
      }

      if (self.toBeUploaded.length > 0) {
        self.submitFiles();
      }
    },
    submitFiles() {
      const self = this;

      const data = new FormData();

      $event.$emit('preloader.on');

      for (const file of self.toBeUploaded) {
        const fileUUID = uuid();

        data.append(file.name, file);
        data.append(`${file.name}_uuid`, fileUUID);
      }

      self.toBeUploaded = [];

      this.$store
        .dispatch('addTemplate', data)
        .then((response) => {
          $event.$emit('preloader.off');
          for (const template of response.data.data) {
            this.$store.commit('addTemplate', template);
          }
          this.$notify({
            group: 'general',
            type: 'success',
            title: 'Success!',
            duration: 5000,
            text: 'Successfully added template.',
          });
        })
        .catch((error) => {
          this.$notify({
            group: 'general',
            type: 'error',
            title: 'Error',
            duration: 5000,
            text: `Could not add template, error: ${error.data.message}.`,
          });
        });
    },
    deleteTemplate(template) {
      $event.$emit('preloader.on');
      this.$store
        .dispatch('deleteTemplate', template.id)
        .then((response) => {
          $event.$emit('preloader.off');
          this.$store.commit('deleteGeneric', {
            objectsName: 'templates',
            idToDelete: template.id,
          });
          this.$notify({
            group: 'general',
            type: 'success',
            title: 'Success!',
            duration: 5000,
            text: 'Template successfully deleted.',
          });
        })
        .catch((error) => {
          $event.$emit('preloader.off');
          if (error.response !== undefined && error.response.status === 403) {
            this.$notify({
              group: 'general',
              type: 'error',
              title: 'Permission Denied',
              duration: 5000,
              text: 'You do not have permission to delete this template. Please contact your admin if you require this permission.',
            });
          } else {
            this.$notify({
              group: 'general',
              type: 'error',
              title: 'Error',
              duration: 5000,
              text: error.data.message,
            });
          }
        });
    },
  },
};
</script>
